/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { AppBar, Box, Button, Step, StepLabel, Stepper, Tooltip, useMediaQuery } from '@mui/material';
import { type ReactNode, useContext, useEffect, useMemo } from 'react';

import { tabs } from '../../constants/tabs';
import { IconCloseBuilder, LogoCKEditor5 } from '../../assets';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import { show } from '@ebay/nice-modal-react';
import { GTMContext } from '../../contexts/GTMContext';
import { CustomerPortalContext } from '../../contexts/CustomerPortalContext';
import type { PluginSelectionMode } from '../PluginSelection/types';
import { GTM_EVENT_CLOSE, GTM_EVENT_LOGO, GTM_EVENT_STEP } from '../../constants/GTMEventNames';
import { AccountInfoDropdown } from './AccountInfoDropdown/AccountInfoDropdown';
import { ResponsiveToolbar } from './ResponsiveToolbar/ResponsiveToolbar';

const REDIRECTS: Record<string, string> = {
	'docs': 'https://ckeditor.com/docs/ckeditor5/',
	// @ts-expect-error variable is injected by vite.
	'portal': CUSTOMER_PORTAL_URL
};
const DEFAULT_REDIRECT = 'https://ckeditor.com/';

const logoButtonStyles = { width: '131px', minWidth: '131px', height: '25px' };
const infoContainerStyles = { display: 'flex', alignItems: 'center', gap: '20px' };
const closeButtonStyles = { color: 'text.secondary', cursor: 'pointer' };
const stepperStyles = { width: '100%', maxWidth: 'var(--stepper-width)' };

type TopBarProps = {
	activeTabIndex: number;
	onTabChange: ( newValue: number ) => void;
	appBarHeight: string;
	isNavigationDisabled: boolean;
	pluginSelectionView: PluginSelectionMode;
	isSelectedPresetModified: boolean;
};

export function TopBar( {
	activeTabIndex,
	onTabChange,
	appBarHeight,
	isNavigationDisabled,
	pluginSelectionView,
	isSelectedPresetModified
}: TopBarProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );
	const { isLoggedIn } = useContext( CustomerPortalContext );
	const doubleRowViewportSize = useMediaQuery( '(width < 1280px)' );

	const preloadErrorCallback = useMemo( () => {
		return () => {
			window.removeEventListener( 'vite:preloadError' as any, preloadErrorCallback );

			window.location.reload();
		};
	}, [] );

	useEffect( () => {
		window.addEventListener( 'vite:preloadError' as any, preloadErrorCallback );

		return () => {
			window.removeEventListener( 'vite:preloadError' as any, preloadErrorCallback );
		};
	}, [ preloadErrorCallback ] );

	async function handleOnLogoClick() {
		const onAccept = () => {
			window.location.assign( DEFAULT_REDIRECT );
		};

		triggerGTMEvent( { event: GTM_EVENT_LOGO } );

		await showConfirmationModal( { onAccept, isSelectedPresetModified } );
	}

	async function handleOnCloseClick() {
		const searchParams = new URLSearchParams( window.location.search );
		const redirectParam = searchParams.get( 'redirect' );

		const onAccept = () => {
			if ( redirectParam && redirectParam in REDIRECTS ) {
				window.location.assign( REDIRECTS[ redirectParam ] );
			} else {
				window.location.assign( DEFAULT_REDIRECT );
			}
		};

		const activeTab = tabs.find( tab => activeTabIndex === tab.index )!;

		triggerGTMEvent( {
			event: GTM_EVENT_CLOSE,
			stage: activeTab.tabName,
			view: activeTab.tabName === 'Features' ? pluginSelectionView : undefined
		} );

		await showConfirmationModal( { onAccept, isSelectedPresetModified } );
	}

	function handleOnStepClick( newTabIndex: number ) {
		if ( isNavigationDisabled ) {
			return;
		}

		onTabChange( newTabIndex );

		const newTab = tabs.find( tab => newTabIndex === tab.index )!;

		triggerGTMEvent( {
			event: GTM_EVENT_STEP,
			stage: newTab.tabName,
			view: newTab.tabName === 'Features' ? pluginSelectionView : undefined
		} );
	}

	return (
		<AppBar
			position={ 'fixed' }
			sx={ theme => ( { height: appBarHeight, background: '#fff', zIndex: theme.zIndex.drawer + 1 } ) }
		>
			<ResponsiveToolbar
				shouldDisplayTwoRows={ doubleRowViewportSize }
				Logo={
					<Button style={ logoButtonStyles } onClick={ handleOnLogoClick }>
						<img src={ LogoCKEditor5 }/>
					</Button>
				}
				InfoButtons={
					<Box sx={ infoContainerStyles }>
						{ isLoggedIn && <AccountInfoDropdown /> }
						<img
							src={ IconCloseBuilder }
							style={ closeButtonStyles }
							onClick={ handleOnCloseClick }
							data-testid="topbar-close-button"
						/>
					</Box>
				}
				Stepper={
					<Stepper
						activeStep={ activeTabIndex }
						nonLinear
						alternativeLabel={ doubleRowViewportSize }
						sx={ stepperStyles }
					>
						{ tabs.map( tab => (
							<Tooltip
								key={ tab.tabName }
								disableHoverListener={ !isNavigationDisabled }
								title="Select one of the presets on the left in order to jump between steps."
							>
								<Step
									completed={ activeTabIndex > tab.index }
									onClick={ () => handleOnStepClick( tab.index ) }
									sx={ !isNavigationDisabled ? { cursor: 'pointer' } : {} }
								>
									<StepLabel>{ tab.tabName }</StepLabel>
								</Step>
							</Tooltip>
						) ) }
					</Stepper>
				}
			/>
		</AppBar>
	);
}

function showConfirmationModal( { onAccept, isSelectedPresetModified }: { onAccept: () => void; isSelectedPresetModified: boolean } ) {
	return show( ConfirmationModal, {
		title: 'Are you sure you want to leave this page?',
		description: isSelectedPresetModified ? 'All changes will be lost.' : null,
		acceptButtonText: 'Leave page',
		declineButtonText: 'Stay on page',
		onAccept
	} );
}
